import { client } from '../services/apollo';
import { GET_PLATFORM_SITE_MARKERS } from '../services/platformSettingsService/queries';
import { normalizeReferralSiteMarker } from './referralRedirectUrl';

const cacheKey = 'platformSiteMarkers';
const defaultPlatformSiteMarkers = ['autopilotee.com'];

export function normalizePlatformSiteMarkers(values?: string[] | null): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values ?? []) {
    const marker = normalizeReferralSiteMarker(value);
    if (!marker || seen.has(marker)) continue;
    seen.add(marker);
    normalized.push(marker);
  }

  return normalized;
}

export function getFallbackPlatformSiteMarkers(): string[] {
  const envValue = process.env.REACT_APP_PLATFORM_SITE_MARKERS;
  const fromEnv = normalizePlatformSiteMarkers(envValue?.split(',') ?? []);
  if (fromEnv.length > 0) return fromEnv;

  const fromCurrentHost = normalizeReferralSiteMarker(
    process.env.REACT_APP_SITE_HOSTNAME || window.location.host,
  );
  if (fromCurrentHost) return [fromCurrentHost];

  return defaultPlatformSiteMarkers;
}

export function getCachedPlatformSiteMarkers(): string[] {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return getFallbackPlatformSiteMarkers();
    const parsed = JSON.parse(raw);
    const markers = normalizePlatformSiteMarkers(Array.isArray(parsed) ? parsed : []);
    return markers.length > 0 ? markers : getFallbackPlatformSiteMarkers();
  } catch {
    return getFallbackPlatformSiteMarkers();
  }
}

export async function refreshPlatformSiteMarkers(): Promise<string[]> {
  try {
    const { data } = await client.query({
      query: GET_PLATFORM_SITE_MARKERS,
      fetchPolicy: 'network-only',
    });
    const markers = normalizePlatformSiteMarkers(data?.platformSiteMarkers);
    if (markers.length > 0) {
      sessionStorage.setItem(cacheKey, JSON.stringify(markers));
      return markers;
    }
  } catch (error) {
    console.warn('Failed to fetch platform site markers; using fallback.', error);
  }

  return getCachedPlatformSiteMarkers();
}
